<template>
  <p v-if="isLoading" class="tw-text-sm tw-text-gray-500">Fetching brand kits...</p>
  <BrandKitGoToSelectPrompt
    v-else-if="brandKits.length === 0"
    message="Create a brand kit to apply your brand's colors, fonts, assets and voice to your registration page and event stage experience."
    buttonText="Create Brand kit"
    @goToSelectBrandKit="handleGoToCreateBrandKit"
  />
  <StudioSelect
    v-else
    id="brand-kit-select"
    placeholder="Select a brand kit"
    :options="brandKitOptions"
    :value="selectedBrandKitId"
    class="tw-text-sm"
    @change="handleBrandKitChange"
  />
</template>

<script setup lang="ts">
import { BrandKit } from '@goldcast/api/backend';
import { onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import BrandKitGoToSelectPrompt from '@/studio/lookAndFeel/components/BrandKitGoToSelectPrompt.vue';
import StudioSelect, { Option } from '@/studioComponents/StudioSelect.vue';

const store = useStore();
const router = useRouter();

const brandKits = computed<BrandKit[]>(() => store.state.studioBrandKit.brandKits);
const selectedBrandKit = computed<BrandKit | null>(() => store.state.studioBrandKit.selectedBrandKit);
const brandKitOptions = computed<Option[]>(() =>
  brandKits.value.map(brandKit => ({
    value: brandKit.id!,
    label: brandKit.name
  }))
);
const selectedBrandKitId = computed<string>(() => selectedBrandKit.value?.id || '');
const isLoading = computed<boolean>(() => store.state.studioBrandKit.isLoading);

function refetchBrandKitsOnTabActive() {
  if (!document.hidden) {
    store.dispatch('studioBrandKit/fetchBrandKits');
  }
}

onMounted(() => {
  store.dispatch('studioBrandKit/ensureBrandKitsLoaded');
  document.addEventListener('visibilitychange', refetchBrandKitsOnTabActive);
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', refetchBrandKitsOnTabActive);
});

function handleGoToCreateBrandKit() {
  const route = router.resolve({ name: 'Brand-Kit' });
  window.open(route.href, '_blank');
}

function handleBrandKitChange(event: Event) {
  const target = event.target as HTMLSelectElement;
  const selectedKit = brandKits.value.find(bk => bk.id === target.value);
  if (selectedKit) {
    store.commit('studioBrandKit/setSelectedBrandKit', selectedKit);
  }
}
</script>
